import { useEffect, useState } from "react";
import {
  PODCAST_STATUS_LABELS,
  PODCAST_STATUS_STYLES,
} from "../components/podcastUi";
import {
  ARTICLE_GEN_URL,
  authFetch,
  emailIntelFetch,
  getToken,
} from "../lib/api";
import { SERVICE_META } from "../lib/serviceMeta";
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle,
  ChevronDown,
  ClipboardList,
  Edit3,
  Eye,
  FileText,
  Filter,
  Gauge,
  Loader2,
  Mail,
  MessageSquare,
  Mic,
  Radio,
  Send,
  Shield,
  TrendingUp,
  Users,
  Volume2,
  Zap,
} from "lucide-react";
import { STATUS_LABELS } from "../portals/ArticleGeneratorPortal";

const TEAM_COLORS = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
  "#ec4899",
];
const TYPE_COLORS = {
  door: "#3b82f6",
  call: "#10b981",
  message: "#f59e0b",
  contact: "#8b5cf6",
  survey: "#6366f1",
  other: "#94a3b8",
};
const TYPE_LABELS = {
  door: "Door Knock",
  call: "Phone Call",
  message: "Message",
  contact: "Contact",
  survey: "Survey Share",
  other: "Other",
};

const days30 = (() => {
  const arr = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    arr.push(d.toISOString().slice(0, 10));
  }
  return arr;
})();

const SpinnerInline = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

const SurveyResponsesSection = ({ facility }) => {
  const fid = facility.facility_id;
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(new Set());

  useEffect(() => {
    authFetch(`/surveys?facility_id=${encodeURIComponent(fid)}`)
      .then((d) =>
        setSurveys(
          Array.isArray(d) ? d.filter((s) => s.audience === "constituent") : [],
        ),
      )
      .catch(() => setSurveys([]))
      .finally(() => setLoading(false));
  }, [fid]);

  const toggle = (survey) => {
    if (expanded === survey.id) {
      setExpanded(null);
      return;
    }
    setExpanded(survey.id);
    if (detail[survey.id]) return;
    setLoadingDetail((p) => new Set([...p, survey.id]));
    Promise.all([
      authFetch(`/surveys/${survey.id}`),
      authFetch(`/surveys/${survey.id}/responses`),
    ])
      .then(([meta, resp]) =>
        setDetail((p) => ({
          ...p,
          [survey.id]: {
            schema: meta?.schema || [],
            responses: resp?.responses || [],
          },
        })),
      )
      .catch(() =>
        setDetail((p) => ({
          ...p,
          [survey.id]: { schema: [], responses: [] },
        })),
      )
      .finally(() =>
        setLoadingDetail((p) => {
          const n = new Set(p);
          n.delete(survey.id);
          return n;
        }),
      );
  };

  if (loading || surveys.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
        <ClipboardList className="w-4 h-4 text-indigo-500" />
        Survey Responses
      </h4>
      {surveys.map((survey) => {
        const isOpen = expanded === survey.id;
        const d = detail[survey.id];
        const isLoading = loadingDetail.has(survey.id);
        return (
          <div
            key={survey.id}
            className="border border-slate-100 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggle(survey)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-sm font-medium text-slate-800 truncate">
                  {survey.title || "Untitled survey"}
                </span>
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${survey.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}
                >
                  {survey.status}
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                <span className="text-sm font-bold text-indigo-600">
                  {survey.response_count ?? 0}
                  <span className="text-xs font-normal text-slate-400 ml-1">
                    responses
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-slate-100 px-4 py-4 bg-slate-50 space-y-5">
                {isLoading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-indigo-400" />
                  </div>
                ) : !d || d.responses.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-3">
                    No responses yet.
                  </p>
                ) : (
                  d.schema.map((q) => {
                    if (q.type === "single_select" && q.options?.length) {
                      const counts = {};
                      q.options.forEach((o) => (counts[o] = 0));
                      d.responses.forEach((r) => {
                        const a = r.answers?.[q.id];
                        if (a != null && counts[a] !== undefined) counts[a]++;
                      });
                      const total = Object.values(counts).reduce(
                        (s, c) => s + c,
                        0,
                      );
                      return (
                        <div key={q.id}>
                          <p className="text-xs font-semibold text-slate-600 mb-2">
                            {q.label}
                          </p>
                          <div className="space-y-1.5">
                            {q.options.map((opt) => {
                              const pct = total
                                ? Math.round((counts[opt] / total) * 100)
                                : 0;
                              return (
                                <div key={opt}>
                                  <div className="flex justify-between text-xs text-slate-600 mb-0.5">
                                    <span>{opt}</span>
                                    <span className="font-medium">
                                      {counts[opt]} ({pct}%)
                                    </span>
                                  </div>
                                  <div className="w-full bg-slate-200 rounded-full h-1.5">
                                    <div
                                      className="bg-indigo-500 h-1.5 rounded-full"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    const texts = d.responses
                      .map((r) => r.answers?.[q.id])
                      .filter(Boolean);
                    if (!texts.length) return null;
                    return (
                      <div key={q.id}>
                        <p className="text-xs font-semibold text-slate-600 mb-2">
                          {q.label}
                        </p>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {texts.map((t, i) => (
                            <p
                              key={i}
                              className="text-xs text-slate-600 bg-white border border-slate-100 rounded px-2 py-1"
                            >
                              {t}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TeamInteractionsSection = ({ facility }) => {
  const fid = facility.facility_id;
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedId, setSelectedId] = useState("all");
  const [byType, setByType] = useState(false);
  const [chartRows, setChartRows] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  useEffect(() => {
    setLoadingTeams(true);
    authFetch(
      `/analytics/team-interactions?facility_id=${encodeURIComponent(fid)}`,
    )
      .then((res) => setTeams(res.teams || []))
      .catch(() => setTeams([]))
      .finally(() => setLoadingTeams(false));
  }, [fid]);

  useEffect(() => {
    if (loadingTeams) return;
    setLoadingChart(true);
    const p = new URLSearchParams({ facility_id: fid, team_id: selectedId });
    if (byType) p.set("by_type", "1");
    authFetch(`/analytics/team-interactions?${p}`)
      .then((res) => setChartRows(res.daily || []))
      .catch(() => setChartRows([]))
      .finally(() => setLoadingChart(false));
  }, [fid, selectedId, byType, loadingTeams]);

  // Build stacked days
  const { stackedDays, series, maxTotal } = (() => {
    if (byType) {
      const types = [...new Set(chartRows.map((r) => r.type))].filter(Boolean);
      const byDay = {};
      chartRows.forEach((r) => {
        if (!byDay[r.day]) byDay[r.day] = {};
        byDay[r.day][r.type] = r.count;
      });
      const stacked = days30.map((day) => {
        const segs = types.map((t) => ({
          key: t,
          count: byDay[day]?.[t] || 0,
          color: TYPE_COLORS[t] || "#94a3b8",
        }));
        return {
          day,
          segments: segs,
          total: segs.reduce((s, x) => s + x.count, 0),
        };
      });
      const mx = Math.max(1, ...stacked.map((d) => d.total));
      return {
        stackedDays: stacked,
        series: types.map((t) => ({
          key: t,
          label: TYPE_LABELS[t] || t,
          color: TYPE_COLORS[t] || "#94a3b8",
        })),
        maxTotal: mx,
      };
    } else if (selectedId === "all") {
      const byDay = {};
      chartRows.forEach((r) => {
        if (!byDay[r.day]) byDay[r.day] = {};
        byDay[r.day][r.team_id] = r.count;
      });
      const stacked = days30.map((day) => {
        const segs = teams.map((t, i) => ({
          key: t.id,
          count: byDay[day]?.[t.id] || 0,
          color: TEAM_COLORS[i % TEAM_COLORS.length],
        }));
        return {
          day,
          segments: segs,
          total: segs.reduce((s, x) => s + x.count, 0),
        };
      });
      const mx = Math.max(1, ...stacked.map((d) => d.total));
      return {
        stackedDays: stacked,
        series: teams.map((t, i) => ({
          key: t.id,
          label: t.name,
          color: TEAM_COLORS[i % TEAM_COLORS.length],
        })),
        maxTotal: mx,
      };
    } else {
      const tidx = teams.findIndex((t) => String(t.id) === String(selectedId));
      const color = TEAM_COLORS[tidx % TEAM_COLORS.length] || TEAM_COLORS[0];
      const byDay = {};
      chartRows.forEach((r) => {
        byDay[r.day] = r.count;
      });
      const stacked = days30.map((day) => ({
        day,
        segments: [{ key: "c", count: byDay[day] || 0, color }],
        total: byDay[day] || 0,
      }));
      const mx = Math.max(1, ...stacked.map((d) => d.total));
      return { stackedDays: stacked, series: [], maxTotal: mx };
    }
  })();

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-baseline justify-between mb-4">
        <h4 className="text-sm font-semibold text-slate-900">Teams</h4>
        <span className="text-xs text-slate-400">Last 30 days</span>
      </div>

      {loadingTeams ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
          <SpinnerInline /> Loading teams…
        </div>
      ) : teams.length === 0 ? (
        <p className="text-sm text-slate-400 py-2">
          No teams found for this facility.
        </p>
      ) : (
        <>
          {/* Team pills */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setSelectedId("all")}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                selectedId === "all"
                  ? "bg-slate-800 text-white border-slate-800"
                  : "text-slate-500 border-slate-300 hover:border-slate-500"
              }`}
            >
              All Teams
            </button>
            {teams.map((t, i) => {
              const color = TEAM_COLORS[i % TEAM_COLORS.length];
              const active = String(selectedId) === String(t.id);
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedId(t.id)}
                  className="px-3 py-1 rounded-full text-xs font-semibold border transition-colors"
                  style={
                    active
                      ? {
                          backgroundColor: color,
                          borderColor: color,
                          color: "#fff",
                        }
                      : { borderColor: color, color }
                  }
                >
                  {t.name} · {t.total_interactions}
                </button>
              );
            })}
          </div>

          {/* View toggle */}
          <div className="flex gap-1 mb-4 bg-slate-100 rounded-lg p-1 w-fit">
            <button
              onClick={() => setByType(false)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${!byType ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Interactions
            </button>
            <button
              onClick={() => setByType(true)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${byType ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              By Type
            </button>
          </div>

          {/* Chart */}
          {loadingChart ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
              <SpinnerInline /> Loading…
            </div>
          ) : (
            <>
              <div className="text-right text-xs text-slate-400 mb-1">
                {maxTotal}
              </div>
              <div className="flex items-end gap-px" style={{ height: "96px" }}>
                {stackedDays.map((d) => {
                  const barPct = maxTotal > 0 ? (d.total / maxTotal) * 100 : 0;
                  return (
                    <div
                      key={d.day}
                      className="flex-1 flex flex-col justify-end group relative"
                      style={{ height: "100%" }}
                    >
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                        {d.day.slice(5)}: {d.total}
                      </div>
                      <div
                        className="w-full flex flex-col-reverse overflow-hidden rounded-t"
                        style={{
                          height: `${Math.max(barPct, d.total > 0 ? 2 : 0)}%`,
                        }}
                      >
                        {d.segments.map((seg) => (
                          <div
                            key={seg.key}
                            style={{
                              height:
                                d.total > 0
                                  ? `${(seg.count / d.total) * 100}%`
                                  : "0%",
                              backgroundColor: seg.color,
                              minHeight: seg.count > 0 ? "2px" : "0",
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-px mt-1">
                {days30.map((day, i) => (
                  <div key={day} className="flex-1 text-center">
                    {i % 5 === 0 && (
                      <span className="text-[9px] text-slate-400">
                        {day.slice(5)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {series.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-3">
                  {series.map((s) => (
                    <div key={s.key} className="flex items-center gap-1.5">
                      <div
                        className="w-2.5 h-2.5 rounded-sm"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="text-xs text-slate-600">{s.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

// ── period → conversations-endpoint period mapping ────────────────
const CONV_PERIOD_MAP = { "1m": "30d", "3m": "90d", "6m": "90d", all: "all" };
const DASH_ROUTE_COLORS = {
  scheduled: "#94a3b8",
  in_progress: "#3b6cf6",
  completed: "#12a150",
  cancelled: "#e5484d",
};
const DASH_ROUTE_LABELS = {
  scheduled: "Scheduled",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};
const DASH_PALETTE = ["#3b6cf6", "#7c5cff", "#12b3a6", "#e8a33d", "#e5484d"];

// ── Per-service statistics tabs (shown when facility has the service) ──

const SERVICE_STAT_TAB_IDS = [
  "telegram_bot",
  "article",
  "podcast",
  "email_intelligence",
];

const SvcSLabel = ({ children }) => (
  <div className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mt-6 mb-2.5">
    {children}
  </div>
);

const SvcKpiCard = ({ icon: Icon, label, value, sub, iconBg, iconClass }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-1">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-semibold text-slate-500">{label}</span>
      {Icon && (
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: iconBg || "#eaf0ff" }}
        >
          <Icon className={`w-3.5 h-3.5 ${iconClass || "text-blue-600"}`} />
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-slate-900 leading-none">
      {value}
    </div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </div>
);

const SvcBarRow = ({ label, count, max, barClass }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm text-slate-600 capitalize">{label}</span>
      <span className="text-sm font-medium text-slate-900">
        {count.toLocaleString()}
      </span>
    </div>
    <div className="w-full bg-slate-100 rounded-full h-1.5">
      <div
        className={`${barClass || "bg-blue-500"} h-1.5 rounded-full transition-all`}
        style={{ width: `${Math.round((count / Math.max(1, max)) * 100)}%` }}
      />
    </div>
  </div>
);

const SvcSkeletonGrid = ({ n = 4 }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
    {[...Array(n)].map((_, i) => (
      <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />
    ))}
  </div>
);

const SvcEmpty = ({ children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm text-sm text-slate-400 text-center">
    {children}
  </div>
);

const TG_SEVERITY_ORDER = ["critical", "high", "medium", "low"];
const TG_SEVERITY_BAR = {
  critical: "bg-red-500",
  high: "bg-orange-500",
  medium: "bg-amber-400",
  low: "bg-slate-400",
};

const TelegramStatsTab = ({ facility }) => {
  const fid = facility.facility_id;
  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);
  const [violations, setViolations] = useState([]);
  const [violationsTotal, setViolationsTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = encodeURIComponent(fid);
    Promise.all([
      authFetch(`/tg-posts?facility_id=${q}&limit=100`),
      authFetch(`/tg-violations?facility_id=${q}&limit=100`),
    ])
      .then(([p, v]) => {
        const postRows = Array.isArray(p?.posts) ? p.posts : [];
        const vioRows = Array.isArray(v?.violations) ? v.violations : [];
        setPosts(postRows);
        setPostsTotal(p?.total ?? postRows.length);
        setViolations(vioRows);
        setViolationsTotal(v?.total ?? vioRows.length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fid]);

  if (loading) return <SvcSkeletonGrid />;

  const unreviewed = violations.filter((v) => !v.reviewed).length;
  const severe = violations.filter(
    (v) => v.severity === "high" || v.severity === "critical",
  ).length;
  const bySeverity = TG_SEVERITY_ORDER.map((s) => ({
    severity: s,
    count: violations.filter((v) => v.severity === s).length,
  })).filter((r) => r.count > 0);
  const maxSev = Math.max(1, ...bySeverity.map((r) => r.count));
  const recentNote =
    violationsTotal > violations.length
      ? `of latest ${violations.length}`
      : null;

  return (
    <div className="space-y-1">
      <SvcSLabel>Telegram Bot</SvcSLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SvcKpiCard
          icon={Send}
          label="Posts Logged"
          value={postsTotal.toLocaleString()}
        />
        <SvcKpiCard
          icon={Shield}
          label="Violations"
          value={violationsTotal.toLocaleString()}
          iconBg="#fef2f2"
          iconClass="text-red-500"
        />
        <SvcKpiCard
          icon={Eye}
          label="Unreviewed"
          value={unreviewed.toLocaleString()}
          sub={recentNote}
          iconBg="#fffbeb"
          iconClass="text-amber-500"
        />
        <SvcKpiCard
          icon={AlertTriangle}
          label="High / Critical"
          value={severe.toLocaleString()}
          sub={recentNote}
          iconBg="#fff7ed"
          iconClass="text-orange-500"
        />
      </div>

      {bySeverity.length > 0 && (
        <>
          <SvcSLabel>Violations by Severity</SvcSLabel>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
            {bySeverity.map((r) => (
              <SvcBarRow
                key={r.severity}
                label={r.severity}
                count={r.count}
                max={maxSev}
                barClass={TG_SEVERITY_BAR[r.severity]}
              />
            ))}
          </div>
        </>
      )}

      {posts.length > 0 && (
        <>
          <SvcSLabel>Recent Posts</SvcSLabel>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-1.5">
            {posts.slice(0, 5).map((p) => (
              <div
                key={p.id}
                className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 text-xs"
              >
                <span className="font-medium text-slate-700 flex-shrink-0">
                  {p.sender_name || "Unknown"}
                </span>
                <span className="text-slate-500 line-clamp-1 flex-1">
                  {p.notes || ""}
                </span>
                <span className="text-slate-400 flex-shrink-0">
                  {p.created_at
                    ? new Date(p.created_at).toLocaleDateString()
                    : ""}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {postsTotal === 0 && violationsTotal === 0 && (
        <SvcEmpty>No Telegram activity recorded yet.</SvcEmpty>
      )}
    </div>
  );
};

const ArticleStatsTab = ({ facility }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setLoading(true);
    setFailed(false);
    const token = getToken();
    fetch(`${ARTICLE_GEN_URL}/articles`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .then((data) => setArticles(Array.isArray(data) ? data : []))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [facility.facility_id]);

  if (loading) return <SvcSkeletonGrid />;
  if (failed) return <SvcEmpty>Could not load article statistics.</SvcEmpty>;

  const byStatus = { draft: 0, in_review: 0, approved: 0 };
  articles.forEach((a) => {
    if (byStatus[a.status] !== undefined) byStatus[a.status]++;
  });
  const scores = articles
    .map((a) => a.grounding?.score)
    .filter((s) => typeof s === "number");
  const avgScore = scores.length
    ? scores.reduce((x, y) => x + y, 0) / scores.length
    : null;
  const flagged = articles.filter((a) => a.grounding?.flagged).length;

  return (
    <div className="space-y-1">
      <SvcSLabel>Article Generator</SvcSLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SvcKpiCard
          icon={FileText}
          label="Total Articles"
          value={articles.length.toLocaleString()}
        />
        <SvcKpiCard
          icon={CheckCircle}
          label="Approved"
          value={byStatus.approved.toLocaleString()}
          iconBg="#ecfdf5"
          iconClass="text-emerald-600"
        />
        <SvcKpiCard
          icon={Eye}
          label="In Review"
          value={byStatus.in_review.toLocaleString()}
          iconBg="#fffbeb"
          iconClass="text-amber-500"
        />
        <SvcKpiCard
          icon={Edit3}
          label="Drafts"
          value={byStatus.draft.toLocaleString()}
          iconBg="#f1f5f9"
          iconClass="text-slate-500"
        />
      </div>

      <SvcSLabel>Content Quality</SvcSLabel>
      <div className="grid grid-cols-2 gap-3">
        <SvcKpiCard
          icon={Gauge}
          label="Avg KB Grounding"
          value={
            avgScore == null
              ? "—"
              : avgScore <= 1
                ? `${Math.round(avgScore * 100)}%`
                : avgScore.toFixed(1)
          }
          sub="how well drafts cite your knowledge base"
        />
        <SvcKpiCard
          icon={AlertTriangle}
          label="Flagged by Guardrails"
          value={flagged.toLocaleString()}
          iconBg="#fff7ed"
          iconClass="text-orange-500"
        />
      </div>

      {articles.length > 0 ? (
        <>
          <SvcSLabel>Recent Articles</SvcSLabel>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-1.5">
            {articles.slice(0, 5).map((a) => {
              const st = STATUS_LABELS[a.status] || STATUS_LABELS.draft;
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 text-xs"
                >
                  <span className="text-slate-700 line-clamp-1 flex-1">
                    {a.topic}
                  </span>
                  <span
                    className={`flex-shrink-0 px-2 py-0.5 rounded-full font-medium ${st.color}`}
                  >
                    {st.label}
                  </span>
                  <span className="text-slate-400 flex-shrink-0">
                    {a.created_at
                      ? new Date(a.created_at).toLocaleDateString()
                      : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <SvcEmpty>No articles generated yet.</SvcEmpty>
      )}
    </div>
  );
};

const PodcastStatsTab = ({ facility }) => {
  const fid = facility.facility_id;
  const [shows, setShows] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = encodeURIComponent(fid);
    Promise.all([
      authFetch(`/podcast/shows?facility_id=${q}`),
      authFetch(`/podcast/episodes?facility_id=${q}`),
    ])
      .then(([s, e]) => {
        setShows(Array.isArray(s?.shows) ? s.shows : []);
        setEpisodes(Array.isArray(e?.episodes) ? e.episodes : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fid]);

  if (loading) return <SvcSkeletonGrid />;

  const published = episodes.filter((e) => e.status === "published").length;
  const totalSeconds = episodes.reduce(
    (s, e) => s + (e.duration_seconds || 0),
    0,
  );
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.round((totalSeconds % 3600) / 60);
  const audioTime =
    totalSeconds === 0
      ? "0m"
      : hours > 0
        ? `${hours}h ${minutes}m`
        : `${minutes}m`;
  const totalTokens = episodes.reduce((s, e) => s + (e.total_tokens || 0), 0);

  const byStatus = {};
  episodes.forEach((e) => {
    byStatus[e.status] = (byStatus[e.status] || 0) + 1;
  });
  const statusRows = Object.entries(byStatus).sort((a, b) => b[1] - a[1]);
  const maxStatus = Math.max(1, ...statusRows.map(([, c]) => c));

  return (
    <div className="space-y-1">
      <SvcSLabel>AI Podcast</SvcSLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SvcKpiCard
          icon={Mic}
          label="Episodes"
          value={episodes.length.toLocaleString()}
          sub={`across ${shows.length} show${shows.length === 1 ? "" : "s"}`}
          iconBg="#fff1f2"
          iconClass="text-rose-500"
        />
        <SvcKpiCard
          icon={Radio}
          label="Published"
          value={published.toLocaleString()}
          iconBg="#ecfdf5"
          iconClass="text-emerald-600"
        />
        <SvcKpiCard icon={Volume2} label="Audio Generated" value={audioTime} />
        <SvcKpiCard
          icon={Zap}
          label="AI Tokens Used"
          value={totalTokens.toLocaleString()}
          iconBg="#fffbeb"
          iconClass="text-amber-500"
        />
      </div>

      {statusRows.length > 0 && (
        <>
          <SvcSLabel>Episodes by Status</SvcSLabel>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
            {statusRows.map(([status, count]) => (
              <SvcBarRow
                key={status}
                label={PODCAST_STATUS_LABELS[status] || status}
                count={count}
                max={maxStatus}
                barClass="bg-rose-400"
              />
            ))}
          </div>
        </>
      )}

      {episodes.length > 0 ? (
        <>
          <SvcSLabel>Recent Episodes</SvcSLabel>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-1.5">
            {episodes.slice(0, 5).map((e) => (
              <div
                key={e.id}
                className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 text-xs"
              >
                <span className="text-slate-700 line-clamp-1 flex-1">
                  {e.title}
                </span>
                <span className="text-slate-400 flex-shrink-0">
                  {e.show_title}
                </span>
                <span
                  className={`flex-shrink-0 px-2 py-0.5 rounded-full font-medium ${PODCAST_STATUS_STYLES[e.status] || "bg-slate-100 text-slate-600"}`}
                >
                  {PODCAST_STATUS_LABELS[e.status] || e.status}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <SvcEmpty>No podcast episodes yet.</SvcEmpty>
      )}
    </div>
  );
};

const EMAIL_INTEL_PLATFORMS = {
  microsoft365: "Microsoft 365",
  gmail: "Gmail",
  imap: "IMAP",
};

const EmailIntelStatsTab = ({ facility }) => {
  const fid = facility.facility_id;
  const [conn, setConn] = useState(null);
  const [rules, setRules] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([
      emailIntelFetch(`/connections/${encodeURIComponent(fid)}`),
      emailIntelFetch(`/rules?facility_id=${encodeURIComponent(fid)}`),
      emailIntelFetch(`/sender-lists?facility_id=${encodeURIComponent(fid)}`),
    ])
      .then(([c, r, l]) => {
        setConn(c.status === "fulfilled" ? c.value?.connection || null : null);
        setRules(
          r.status === "fulfilled" && Array.isArray(r.value?.rules)
            ? r.value.rules
            : [],
        );
        setLists(
          l.status === "fulfilled" && Array.isArray(l.value?.lists)
            ? l.value.lists
            : [],
        );
      })
      .finally(() => setLoading(false));
  }, [fid]);

  if (loading) return <SvcSkeletonGrid />;

  const enabledRules = rules.filter((r) => r.enabled).length;
  const connStatus = conn?.status || "not connected";
  const connLive = connStatus === "live" || connStatus === "active";

  return (
    <div className="space-y-1">
      <SvcSLabel>Email Intelligence</SvcSLabel>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SvcKpiCard
          icon={Mail}
          label="Connection"
          value={
            <span className="capitalize">{connStatus.replace(/_/g, " ")}</span>
          }
          sub={
            conn?.platform
              ? EMAIL_INTEL_PLATFORMS[conn.platform] || conn.platform
              : null
          }
          iconBg={connLive ? "#ecfdf5" : "#fff7ed"}
          iconClass={connLive ? "text-emerald-600" : "text-orange-500"}
        />
        <SvcKpiCard
          icon={Filter}
          label="Routing Rules"
          value={enabledRules.toLocaleString()}
          sub={`${rules.length} total`}
        />
        <SvcKpiCard
          icon={Users}
          label="Sender Lists"
          value={lists.length.toLocaleString()}
        />
        <SvcKpiCard
          icon={Calendar}
          label="Connected Since"
          value={
            conn?.consented_at
              ? new Date(conn.consented_at).toLocaleDateString()
              : "—"
          }
        />
      </div>

      {!conn && (
        <SvcEmpty>
          No mailbox connection yet — set one up in the Email Intelligence
          portal.
        </SvcEmpty>
      )}

      <p className="text-[11px] text-slate-400 mt-4">
        Per-message volume metrics aren't tracked yet — statistics reflect the
        current connection configuration.
      </p>
    </div>
  );
};

export const UsageAnalyticsDashboard = ({ facility }) => {
  const fid = facility.facility_id;
  const [period, setPeriod] = useState("1m");
  const [svcTab, setSvcTab] = useState("overview");

  // ── chatbot (period-dependent) ──────────────────────────────────
  const [usageData, setUsageData] = useState(null);
  const [convData, setConvData] = useState(null);
  const [loadingChatbot, setLoadingChatbot] = useState(true);

  useEffect(() => {
    setLoadingChatbot(true);
    const q = encodeURIComponent(fid);
    const cp = CONV_PERIOD_MAP[period] || "30d";
    Promise.all([
      authFetch(`/analytics/facility-usage?facility_id=${q}&period=${period}`),
      authFetch(`/analytics/conversations?facility_id=${q}&period=${cp}`),
    ])
      .then(([u, c]) => {
        setUsageData(u?.error ? null : u);
        setConvData(c?.error ? null : c);
      })
      .catch(() => {})
      .finally(() => setLoadingChatbot(false));
  }, [fid, period]);

  // ── static (field ops, surveys, KB gaps) ───────────────────────
  const [feedbackData, setFeedbackData] = useState(null);
  const [walksRaw, setWalksRaw] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [kbGapRows, setKbGapRows] = useState([]);
  const [reviewTasks, setReviewTasks] = useState([]);
  const [topSuccessful, setTopSuccessful] = useState([]);
  const [loadingStatic, setLoadingStatic] = useState(true);

  useEffect(() => {
    setLoadingStatic(true);
    const q = encodeURIComponent(fid);
    Promise.all([
      authFetch(`/analytics/fieldiq-feedback?facility_id=${q}`),
      authFetch(`/facility-walks?facility_id=${q}`),
      authFetch(`/analytics/team-interactions?facility_id=${q}`),
      authFetch(`/surveys?facility_id=${q}`),
      authFetch(`/analytics/knowledge-gaps?facility_id=${q}&period=30d`),
      authFetch(`/rag/review-tasks?facility_id=${q}&status=open`),
      authFetch(`/analytics/top-successful?facility_id=${q}&period=30d`),
    ])
      .then(([fb, w, t, sv, gaps, tasks, ok]) => {
        setFeedbackData(fb?.error ? null : fb);
        setWalksRaw(Array.isArray(w) ? w : w?.walks || []);
        setTeamData(t?.error ? null : t);
        setSurveys(
          Array.isArray(sv)
            ? sv.filter((s) => s.audience === "constituent")
            : [],
        );
        setKbGapRows(Array.isArray(gaps) ? gaps.slice(0, 5) : []);
        setReviewTasks(Array.isArray(tasks) ? tasks : []);
        setTopSuccessful(Array.isArray(ok) ? ok.slice(0, 10) : []);
      })
      .catch(() => {})
      .finally(() => setLoadingStatic(false));
  }, [fid]);

  // ── derived ─────────────────────────────────────────────────────
  const usage = usageData?.summary || {};
  const monthly = [...(usageData?.monthly || [])].reverse();
  const channels = usageData?.channels || [];
  // Exclude the default "widget" channel — it's the implicit default and adds no signal.
  const nonWidgetChannels = channels.filter(
    (c) => (c.channel || "widget") !== "widget",
  );
  const maxMonthly = Math.max(1, ...monthly.map((m) => m.total_queries));

  const topQs = (convData?.top_questions || []).slice(0, 8);
  const kbCoverage =
    convData?.summary?.low_confidence_pct != null
      ? Math.round(100 - parseFloat(convData.summary.low_confidence_pct))
      : null;

  const fbSummary = feedbackData?.summary || {};
  const fbTotal = fbSummary.total || 0;
  const fbUpPct =
    fbTotal > 0 ? Math.round(((fbSummary.thumbs_up || 0) / fbTotal) * 100) : 0;

  const walkByStatus = {
    scheduled: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
  };
  walksRaw.forEach((w) => {
    if (walkByStatus[w.status] !== undefined) walkByStatus[w.status]++;
    else walkByStatus.scheduled++;
  });
  const stopsVisited = walksRaw.reduce((s, w) => s + (w.stops_visited || 0), 0);
  const stopsContacted = walksRaw.reduce(
    (s, w) => s + (w.stops_contacted || 0),
    0,
  );
  const contactRate =
    stopsVisited > 0 ? Math.round((stopsContacted / stopsVisited) * 100) : null;

  const topTeams = [...(teamData?.teams || [])]
    .sort((a, b) => (b.total_interactions || 0) - (a.total_interactions || 0))
    .slice(0, 3);
  const maxTeam = Math.max(
    1,
    ...topTeams.map((t) => t.total_interactions || 0),
  );

  const totalSurveyResponses = surveys.reduce(
    (s, sv) => s + (sv.response_count || 0),
    0,
  );

  const PERIODS = [
    { id: "1m", label: "1 Month" },
    { id: "3m", label: "3 Months" },
    { id: "6m", label: "6 Months" },
    { id: "all", label: "All Time" },
  ];

  const SLabel = ({ children }) => (
    <div className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mt-6 mb-2.5">
      {children}
    </div>
  );

  const KpiCard = ({
    icon: Icon,
    label,
    value,
    delta,
    deltaDir,
    sub,
    iconBg,
  }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-500">{label}</span>
        {Icon && (
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: iconBg || "#eaf0ff" }}
          >
            <Icon className="w-3.5 h-3.5 text-blue-600" />
          </div>
        )}
      </div>
      <div className="text-2xl font-bold text-slate-900 leading-none">
        {value}
      </div>
      {delta && (
        <div
          className={`text-xs font-medium ${deltaDir === "up" ? "text-emerald-600" : deltaDir === "down" ? "text-red-500" : "text-slate-400"}`}
        >
          {delta}
        </div>
      )}
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );

  const Skeleton = ({ h = "h-24" }) => (
    <div className={`${h} bg-slate-100 rounded-xl animate-pulse`} />
  );

  // ── per-service stats tabs (only for services this facility has) ──
  const svcTabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    ...SERVICE_STAT_TAB_IDS.filter((t) =>
      (facility.services || []).some((s) => s.service_type === t),
    ).map((t) => ({
      id: t,
      label: SERVICE_META[t]?.label || t,
      icon: SERVICE_META[t]?.icon || BarChart3,
    })),
  ];

  const svcTabBar = svcTabs.length > 1 && (
    <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 overflow-x-auto mb-3">
      {svcTabs.map((t) => (
        <button
          key={t.id}
          onClick={() => setSvcTab(t.id)}
          className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            svcTab === t.id
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <t.icon className="w-4 h-4 mr-1.5" />
          {t.label}
        </button>
      ))}
    </div>
  );

  if (svcTab !== "overview") {
    return (
      <div className="space-y-1">
        {svcTabBar}
        {svcTab === "telegram_bot" && <TelegramStatsTab facility={facility} />}
        {svcTab === "article" && <ArticleStatsTab facility={facility} />}
        {svcTab === "podcast" && <PodcastStatsTab facility={facility} />}
        {svcTab === "email_intelligence" && (
          <EmailIntelStatsTab facility={facility} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {svcTabBar}
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Usage Analytics</h3>
          <p className="text-sm text-slate-500">
            {facility.facility_name} · live data
          </p>
        </div>
        <div className="flex space-x-1 bg-white rounded-lg border border-slate-200 p-1">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${period === p.id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══ Section 1: KPIs ══════════════════════════════════════ */}
      <SLabel>Performance</SLabel>
      {loadingChatbot ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            icon={MessageSquare}
            label="Total Queries"
            value={(usage.total_queries || 0).toLocaleString()}
            delta={
              usage.total_queries > 0
                ? `${usage.total_queries} in period`
                : "No queries yet"
            }
            deltaDir="flat"
            iconBg="#eaf0ff"
          />
          <KpiCard
            icon={Users}
            label="Unique Sessions"
            value={(usage.unique_sessions || 0).toLocaleString()}
            sub="distinct chat sessions"
            iconBg="#e7f7ee"
          />
          <KpiCard
            icon={Zap}
            label="Avg Response"
            value={usage.avg_response_ms ? `${usage.avg_response_ms}ms` : "—"}
            sub="median response time"
            iconBg="#f3f0ff"
          />
          <KpiCard
            icon={CheckCircle}
            label="KB Coverage"
            value={kbCoverage != null ? `${kbCoverage}%` : "—"}
            delta={
              reviewTasks.length > 0
                ? `▼ ${reviewTasks.length} gap${reviewTasks.length !== 1 ? "s" : ""} flagged`
                : kbCoverage != null
                  ? "No gaps flagged"
                  : "Awaiting queries"
            }
            deltaDir={reviewTasks.length > 0 ? "down" : "flat"}
            sub="answered confidently"
            iconBg="#fff8ec"
          />
        </div>
      )}

      {/* ══ Section 2: Engagement & Satisfaction ═════════════════ */}
      <SLabel>Engagement &amp; Satisfaction</SLabel>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Queries chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-4">
            <h4 className="text-sm font-semibold text-slate-900">
              Queries Over Time
            </h4>
            <span className="text-xs text-slate-400">by month</span>
          </div>
          {loadingChatbot ? (
            <div className="h-28 animate-pulse bg-slate-100 rounded-lg" />
          ) : monthly.length === 0 ? (
            <div className="h-28 flex items-center justify-center text-sm text-slate-400">
              No queries yet for this period.
            </div>
          ) : (
            <div className="flex items-end gap-2" style={{ height: "110px" }}>
              {monthly.map((m) => (
                <div
                  key={m.month}
                  className="flex-1 flex flex-col items-center justify-end gap-1"
                >
                  <span className="text-[10px] font-medium text-slate-600">
                    {m.total_queries}
                  </span>
                  <div
                    className="w-full rounded-t-md"
                    style={{
                      height: `${Math.max(4, (m.total_queries / maxMonthly) * 80)}px`,
                      background: "linear-gradient(180deg,#5b83f8,#3b6cf6)",
                    }}
                  />
                  <span className="text-[10px] text-slate-400 truncate w-full text-center">
                    {m.month}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Chat feedback */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-900">
              Chat Feedback
            </h4>
            <span className="text-xs text-slate-400">30 days</span>
          </div>
          {loadingStatic ? (
            <div className="h-16 animate-pulse bg-slate-100 rounded-lg" />
          ) : fbTotal === 0 ? (
            <p className="text-sm text-slate-400 py-2">No feedback yet.</p>
          ) : (
            <>
              <div className="flex gap-4 mb-3">
                <div className="flex items-center gap-1.5">
                  <span>👍</span>
                  <div>
                    <div className="text-base font-bold text-emerald-600">
                      {fbSummary.thumbs_up ?? 0}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {fbUpPct}% positive
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>👎</span>
                  <div>
                    <div className="text-base font-bold text-red-500">
                      {fbSummary.thumbs_down ?? 0}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      {100 - fbUpPct}% negative
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className="bg-emerald-500 h-2 rounded-full transition-all"
                  style={{ width: `${fbUpPct}%` }}
                />
              </div>
              {fbTotal < 5 && (
                <p className="text-[11px] text-slate-400 mt-2">
                  Only {fbTotal} rating{fbTotal !== 1 ? "s" : ""} so far —
                  prompt users for more feedback.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* ══ Section 3: Field Operations ══════════════════════════ */}
      <SLabel>Field Operations</SLabel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Routes funnel */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-4">
            <h4 className="text-sm font-semibold text-slate-900">Routes</h4>
            <span className="text-xs text-slate-400">
              {walksRaw.length} total
              {contactRate != null ? ` · ${contactRate}% contact` : ""}
            </span>
          </div>
          {loadingStatic ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="h-5 bg-slate-100 rounded animate-pulse"
                />
              ))}
            </div>
          ) : walksRaw.length === 0 ? (
            <p className="text-sm text-slate-400 py-2">
              No routes created yet.
            </p>
          ) : (
            <div className="space-y-2.5">
              {["scheduled", "in_progress", "completed", "cancelled"].map(
                (s) => {
                  const count = walkByStatus[s] || 0;
                  const pct =
                    walksRaw.length > 0
                      ? Math.round((count / walksRaw.length) * 100)
                      : 0;
                  return (
                    <div key={s}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-600">
                          {DASH_ROUTE_LABELS[s]}
                        </span>
                        <span className="text-xs font-medium text-slate-900">
                          {count}
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{
                            width: `${Math.max(pct, count > 0 ? 2 : 0)}%`,
                            backgroundColor: DASH_ROUTE_COLORS[s],
                          }}
                        />
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>
        {/* Team leaderboard (top 3 by interactions) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-4">
            <h4 className="text-sm font-semibold text-slate-900">
              Team Activity
            </h4>
            <span className="text-xs text-slate-400">last 30 days</span>
          </div>
          {loadingStatic ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-10 bg-slate-100 rounded animate-pulse"
                />
              ))}
            </div>
          ) : topTeams.length === 0 ? (
            <p className="text-sm text-slate-400 py-2">No team activity yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {topTeams.map((team, i) => (
                <div key={team.id} className="flex items-center gap-3 py-2.5">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                    style={{
                      background: DASH_PALETTE[i % DASH_PALETTE.length],
                    }}
                  >
                    {(team.name || "T").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">
                      {team.name}
                    </div>
                    <div className="text-[10px] text-slate-400 mb-1">
                      {team.total_interactions || 0} interactions
                    </div>
                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-1 rounded-full transition-all"
                        style={{
                          width: `${Math.round(((team.total_interactions || 0) / maxTeam) * 100)}%`,
                          background: DASH_PALETTE[i % DASH_PALETTE.length],
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══ Section 4: Surveys ═══════════════════════════════════ */}
      {(surveys.length > 0 || loadingStatic) && (
        <>
          <SLabel>Surveys — Response Data</SLabel>
          {loadingStatic ? (
            <div className="grid grid-cols-3 gap-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} h="h-20" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <KpiCard
                icon={ClipboardList}
                label="Total Responses"
                value={totalSurveyResponses.toLocaleString()}
                sub={`across ${surveys.length} survey${surveys.length !== 1 ? "s" : ""}`}
                iconBg="#eaf0ff"
              />
              <KpiCard
                icon={CheckCircle}
                label="Active Surveys"
                value={surveys.filter((s) => s.status === "published").length}
                sub="published & accepting"
                iconBg="#e7f7ee"
              />
              <KpiCard
                icon={TrendingUp}
                label="Avg / Survey"
                value={
                  surveys.length > 0
                    ? Math.round(totalSurveyResponses / surveys.length)
                    : "—"
                }
                sub="responses per survey"
                iconBg="#f3f0ff"
              />
            </div>
          )}
          <SurveyResponsesSection facility={facility} />
        </>
      )}

      {/* ══ Section 5: Content Quality ═══════════════════════════ */}
      <SLabel>Content Quality &amp; Coverage</SLabel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Top topics */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-900">Top Topics</h4>
            <span className="text-xs text-slate-400">most-asked</span>
          </div>
          {loadingChatbot ? (
            <div className="flex flex-wrap gap-2">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-6 rounded-full animate-pulse bg-slate-100"
                  style={{ width: `${60 + i * 15}px` }}
                />
              ))}
            </div>
          ) : topQs.length === 0 ? (
            <p className="text-sm text-slate-400 py-2">
              No queries logged yet.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-3">
                {topQs.map((q, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                    style={{ background: "#eaf0ff", color: "#3b6cf6" }}
                    title={q.question}
                  >
                    <span
                      className="max-w-[120px] truncate"
                      style={{ display: "inline-block" }}
                    >
                      {q.question}
                    </span>
                    <span
                      className="rounded-full px-1.5 py-px text-[10px] font-bold flex-shrink-0"
                      style={{ background: "#3b6cf6", color: "#fff" }}
                    >
                      {q.count}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400">
                From {(convData?.summary?.total_queries || 0).toLocaleString()}{" "}
                queries in period.
              </p>
            </>
          )}
        </div>
        {/* KB Gaps */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-baseline justify-between mb-3">
            <h4 className="text-sm font-semibold text-slate-900">KB Gaps</h4>
            <span className="text-xs text-slate-400">low-confidence · 30d</span>
          </div>
          {loadingStatic ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <div
                  key={i}
                  className="h-12 bg-slate-100 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : reviewTasks.length === 0 && kbGapRows.length === 0 ? (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-emerald-50">
              <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-emerald-700">
                No open gaps — KB is covering all queries confidently.
              </p>
            </div>
          ) : (
            <>
              {reviewTasks.length > 0 && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-slate-800">
                    <strong className="font-semibold block mb-0.5">
                      {reviewTasks.length} open review task
                      {reviewTasks.length !== 1 ? "s" : ""}
                    </strong>
                    Add answers in the KB to close these gaps.
                  </div>
                </div>
              )}
              {kbGapRows.length > 0 && (
                <div className="space-y-1.5">
                  {kbGapRows.map((g, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 text-xs"
                    >
                      <span className="text-slate-400 font-bold mt-px flex-shrink-0">
                        {i + 1}.
                      </span>
                      <span
                        className="text-slate-600 line-clamp-2 flex-1"
                        title={g.user_question}
                      >
                        {g.user_question}
                      </span>
                      <span className="ml-auto flex-shrink-0 text-slate-400 font-medium">
                        ×{g.times_asked}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Top successful queries */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-baseline justify-between mb-3">
          <h4 className="text-sm font-semibold text-slate-900">
            Top Successful Queries
          </h4>
          <span className="text-xs text-slate-400">
            answered confidently · 30d
          </span>
        </div>
        {loadingStatic ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-10 bg-slate-100 rounded-lg animate-pulse"
              />
            ))}
          </div>
        ) : topSuccessful.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">
            No answered queries yet for this period.
          </p>
        ) : (
          <div className="space-y-1.5">
            {topSuccessful.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-2 p-2 rounded-lg bg-emerald-50/60 text-xs"
              >
                <span className="text-emerald-500 font-bold mt-px flex-shrink-0">
                  {i + 1}.
                </span>
                <span
                  className="text-slate-700 line-clamp-2 flex-1"
                  title={s.user_question}
                >
                  {s.user_question}
                </span>
                {s.similarity_score != null && (
                  <span
                    className="flex-shrink-0 text-emerald-600 font-medium"
                    title="Average KB match score"
                  >
                    {Math.round(s.similarity_score * 100)}%
                  </span>
                )}
                <span className="flex-shrink-0 text-slate-400 font-medium">
                  ×{s.times_asked}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══ Section 6: Channels ══════════════════════════════════ */}
      {nonWidgetChannels.length > 0 && (
        <>
          <SLabel>Channels</SLabel>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="space-y-3">
              {nonWidgetChannels.map((c) => {
                const total = usage.total_queries || 1;
                const pct = Math.round((c.count / total) * 100);
                return (
                  <div key={c.channel}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600 capitalize">
                        {c.channel || "widget"}
                      </span>
                      <span className="text-sm font-medium text-slate-900">
                        {c.count.toLocaleString()} ({pct}%)
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ══ Full team stacked chart ══════════════════════════════ */}
      <TeamInteractionsSection facility={facility} />
    </div>
  );
};

// ── API Keys Management ───────────────────────────────────────────
